/**
 * RegexBuilder — cấu hình kiểm tra mã hồ sơ (MSSV, mã hệ đào tạo, ...)
 * theo nhiều pattern regex cho 1 `ProcedureType`.
 *
 * Lưu trữ dưới dạng JSON string ở
 * [[ProcedureTypeResponse.RegexBuilderJson]] để (1) số lượng pattern
 * không cố định, (2) admin tự khai báo ở UI mà không cần đổi code, và
 * (3) backend trả về nguyên xi chuỗi — toàn bộ parse + match nằm ở
 * client.
 *
 * Ví dụ payload:
 * ```
 * [
 *   { "name": "Thạc sĩ",           "regex": "^C.*" },
 *   { "name": "Tiến sĩ",           "regex": "^N.*" },
 *   { "name": "Đại học chính quy", "regex": "^K.*" },
 *   { "name": "LKQT Đại học",      "regex": "^U.*" }
 * ]
 * ```
 *
 * Mỗi entry vận hành độc lập — chỉ cần MỘT entry match là coi như mã
 * thuộc về loại nghiệp vụ này.
 */

/**1 pattern khai báo bởi admin. */
export type RegexBuilderEntry = {
    /**Tên nghiệp vụ — chỉ để hiển thị / debug, không dùng để match. */
    name: string;
    /**Pattern regex (chuỗi — chưa compile). Cú pháp chuẩn JavaScript. */
    regex: string;
};

/**
 * Parse `RegexBuilderJson` (chuỗi JSON) thành mảng entry. An toàn:
 *  - Input rỗng / null / undefined ⇒ trả `[]`.
 *  - JSON parse fail / không phải mảng ⇒ trả `[]` + log warn (tránh
 *    crash UI khi data từ server bị hỏng).
 *  - Loại bỏ những entry thiếu `regex` (string rỗng).
 */
export const parseRegexBuilderJson = (
    json: string | null | undefined
): RegexBuilderEntry[] => {
    if (!json || typeof json !== 'string' || !json.trim()) return [];
    try {
        const parsed = JSON.parse(json);
        if (!Array.isArray(parsed)) {
            console.warn(
                '[parseRegexBuilderJson] expected array, got:',
                typeof parsed
            );
            return [];
        }
        return parsed
            .filter(
                (x): x is RegexBuilderEntry =>
                    !!x && typeof x === 'object' && typeof x.regex === 'string'
            )
            .map((x) => ({
                name: typeof x.name === 'string' ? x.name : '',
                regex: x.regex,
            }))
            .filter((x) => !!x.regex.trim());
    } catch (err) {
        console.warn('[parseRegexBuilderJson] JSON parse failed:', err);
        return [];
    }
};

/**
 * Serialize danh sách entry về chuỗi JSON để lưu xuống DB qua
 * `RegexBuilderJson`. Mảng rỗng ⇒ trả `null` để DB rõ ràng "không
 * có cấu hình" (vs chuỗi `"[]"` gây confusing khi truy vấn).
 */
export const serializeRegexBuilder = (
    entries: RegexBuilderEntry[] | null | undefined
): string | null => {
    if (!entries?.length) return null;
    // Filter rác (entry không có regex) trước khi serialize để DB sạch.
    const clean = entries
        .map((x) => ({
            name: (x.name ?? '').trim(),
            regex: (x.regex ?? '').trim(),
        }))
        .filter((x) => !!x.regex);
    return clean.length ? JSON.stringify(clean) : null;
};

/**
 * Match `code` (vd: MSSV `C25601276`) với 1 entry. Trả `false` khi
 * pattern không hợp lệ (vd: regex có cú pháp sai) — không throw để
 * loop caller không bị chặn bởi 1 cấu hình sai.
 */
export const matchRegexEntry = (
    entry: RegexBuilderEntry,
    code: string
): boolean => {
    if (!entry?.regex || !code) return false;
    try {
        return new RegExp(entry.regex).test(code);
    } catch (err) {
        console.warn(
            `[matchRegexEntry] invalid regex pattern '${entry.regex}':`,
            err
        );
        return false;
    }
};

/**
 * Kiểm tra `code` có khớp với BẤT KỲ entry nào trong danh sách không.
 * Dùng cho luồng nghiệp vụ "mã này thuộc loại thủ tục X" — chỉ cần 1
 * pattern match là `true`.
 */
export const matchesAnyRegexEntry = (
    entries: RegexBuilderEntry[] | null | undefined,
    code: string
): boolean => {
    if (!entries?.length || !code) return false;
    return entries.some((e) => matchRegexEntry(e, code));
};

/**
 * Trả về tên entry đầu tiên khớp `code`. Null nếu không match. Hữu
 * dụng cho UI muốn hiển thị "Mã này thuộc nghiệp vụ X" thay vì chỉ
 * yes/no.
 */
export const findMatchedRegexEntryName = (
    entries: RegexBuilderEntry[] | null | undefined,
    code: string
): string | null => {
    if (!entries?.length || !code) return null;
    return entries.find((e) => matchRegexEntry(e, e ? code : ''))?.name ?? null;
};

/**
 * Bộ default pattern được liệt kê trong nghiệp vụ ban đầu — KHÔNG
 * auto-populate vào DB. Admin có thể chọn "Áp dụng mẫu" trong modal
 * để khởi tạo nhanh, hoặc copy/paste tay từ đây.
 *
 * Khi nghiệp vụ mở rộng (thêm hệ đào tạo mới), thêm vào mảng này hoặc
 * để admin tự thêm trên UI — không cần đổi code chạy match.
 */
export const REGEX_BUILDER_PRESETS: RegexBuilderEntry[] = [
    { name: 'Thạc sĩ', regex: '^C.*' },
    { name: 'Tiến sĩ', regex: '^N.*' },
    // Đại học chính quy + VB2 chính quy đều bắt đầu bằng K — gộp 1
    // pattern vì cùng quy ước.
    { name: 'Đại học chính quy / VB2 chính quy', regex: '^K(?!.*LTR).*' },
    { name: 'Đại học VB2 VLVH', regex: '^B.*' },
    // Liên trường: bắt đầu K + có 'LTR' bên trong.
    { name: 'Liên trường mã', regex: '^K.*LTR.*' },
    { name: 'LKQT Thạc sĩ', regex: '^M.*' },
    { name: 'LKQT Đại học', regex: '^U.*' },
];
